import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum ToomPurchaseOrderProcurementLineItemKeys {
  itemType = 'itemType',
  number = 'number',
  description = 'description',
  qtyOrdered = 'qtyOrdered',
  uom = 'uom',
  unitCost = 'unitCost',
  totalCost = 'totalCost',
}

const toomPurchaseOrderProcurementAddressSchema = yup.object().shape({
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  contactName: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
})

const toomPurchaseOrderProcurementLineItemSchema = yup.object().shape({
  [ToomPurchaseOrderProcurementLineItemKeys.itemType]: yup.string().nullable().optional(),
  [ToomPurchaseOrderProcurementLineItemKeys.number]: yup.string().nullable().optional(),
  [ToomPurchaseOrderProcurementLineItemKeys.description]: yup.string().nullable().optional(),
  [ToomPurchaseOrderProcurementLineItemKeys.qtyOrdered]: yup.string().nullable().optional(),
  [ToomPurchaseOrderProcurementLineItemKeys.uom]: yup.string().nullable().optional(),
  [ToomPurchaseOrderProcurementLineItemKeys.unitCost]: yup.string().nullable().optional(),
  // totalCost is auto-computed at render time (qtyOrdered × unitCost)
})

export const toomPurchaseOrderProcurementFileDataSchema = yup.object().shape({
  poNumber: yup.string().nullable().optional(),
  issueDate: yup.string().nullable().optional(),
  requestedDelivery: yup.string().nullable().optional(),
  vendor: toomPurchaseOrderProcurementAddressSchema,
  shipTo: toomPurchaseOrderProcurementAddressSchema,
  buyer: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  fobPoint: yup.string().nullable().optional(),
  shippingTerms: yup.string().nullable().optional(),
  shipVia: yup.string().nullable().optional(),
  dateScheduled: yup.string().nullable().optional(),
  poNotes: yup.string().nullable().optional(),
  items: yup.array().of(toomPurchaseOrderProcurementLineItemSchema).required(),
  instructions: yup.string().nullable().optional(),
})

export type ToomPurchaseOrderProcurementFileData = yup.InferType<
  typeof toomPurchaseOrderProcurementFileDataSchema
>
type RequiredFileData = MakeRequiredAndNullable<ToomPurchaseOrderProcurementFileData>

export const toomPurchaseOrderProcurementFileMappings: RequiredFileData = {
  poNumber: '676fb16f-c195-44c6-ad4d-309e19d00837',
  issueDate: '2d60b9b2-aafd-4d0b-b268-8f70977740d5',
  requestedDelivery: '5c13aab6-14a3-4533-b347-3ffdeb5610b0',
  vendor: {
    companyName: '10862d01-53e4-48b4-86cd-74528dbe5f38',
    addressLine1: 'd36d88cb-d1cb-4e7e-bf3e-6405e83f0db3',
    addressLine2: 'b5010055-f1a6-4648-b164-712d7b673f9e',
    city: '8efdba87-9ec8-4943-836c-25e5da23bae5',
    state: 'ee4f6559-f5e2-45cd-b9a7-7ecd8776d2c6',
    zipCode: 'b320d692-7f39-4388-85ae-c2a7df18471e',
    country: '1cbf533d-a612-41c2-a0a5-03843de7c06a',
    contactName: 'ba460605-58f5-48f8-8cdd-210b3dea27db',
    phoneNumber: '1f850b30-e0cd-4604-b90f-9ee67a792c18',
    emailAddress: '3a88d6c3-8dff-477a-a01c-52c4b93dbb5b',
  },
  shipTo: {
    companyName: '3ad2cad1-7a32-40fe-83df-8fc64ec66bee',
    addressLine1: '5c68bd18-a653-4ebe-90ed-56c58ad69084',
    addressLine2: 'a1971de0-a020-46a9-a421-47d0b2fa4223',
    city: 'ae8d31d3-d8f5-468d-ab5f-115ae0532efb',
    state: 'ddaa5c6e-b5e9-4c28-ae58-a196264bfb1a',
    zipCode: '89f5692e-57b6-40dd-858c-dbc93ba470eb',
    country: '7db4c91b-4ead-4f59-8da0-359ec29db46e',
    contactName: '284a7680-9909-436a-ae87-b8e6a7077646',
    phoneNumber: '468f6dee-58b2-4876-975e-d7b0343dc3bd',
    emailAddress: '58c1c10b-1523-47de-b9c5-5cc92d2ad808',
  },
  buyer: '0398e827-45a8-4a17-9dfe-78fc0419ad9f',
  paymentTerms: '0df64d76-9979-4f3b-a238-9daa2efdb781',
  fobPoint: '1cc3576d-c2d0-40a6-9d54-ea32f8f589b9',
  shippingTerms: 'ffeabdab-026d-4e45-acfc-04325ad9d701',
  shipVia: '53a12ed2-c810-44db-904c-8dc0f9bae316',
  dateScheduled: 'e5a93204-33d7-484a-b65e-1ae74f8b73fd',
  poNotes: 'd1abcda5-c378-46e5-862a-a0df7bbe05e2',
  items: [
    {
      itemType: 'ca485402-30a8-4d34-aacd-5c22bee9597d',
      number: '8def7e60-ede0-4a8f-910b-a68588cd9c8c',
      description: 'c7bcfcaa-abdb-4716-b580-9a70800c48eb',
      qtyOrdered: 'e776b1a7-d6ea-4c26-8ed7-3a49789aedbb',
      uom: '1f7110c0-dbfd-4ded-9e70-eddb7ed10ce3',
      unitCost: 'b9acdaf3-d0a3-4499-b695-4fffdf1145d6',
    },
  ],
  instructions: '412b21b9-bad6-4d9a-86a5-5b5f26790abf',
}

export const mockToomPurchaseOrderProcurementData: RequiredFileData = {
  poNumber: 'PO-11',
  issueDate: '07/17/2026',
  requestedDelivery: '07/31/2026',
  vendor: {
    companyName: 'Columbus Oils - Des Plaines',
    addressLine1: '30 East Oakton Street',
    addressLine2: '',
    city: 'Des Plaines',
    state: 'IL',
    zipCode: '60018',
    country: 'United States',
    contactName: 'Marco Favia',
    phoneNumber: '847-257-8920',
    emailAddress: 'mfavia@cvoils.com',
  },
  shipTo: {
    companyName: 'The Fresh Factory - Production',
    addressLine1: '485 Crossroads Pkwy Dock A',
    addressLine2: '',
    city: 'Bolingbrook',
    state: 'IL',
    zipCode: '60440',
    country: 'United States',
    contactName: 'Shipping',
    phoneNumber: '708-254-5906',
    emailAddress: 'orders@thefreshfactory.co',
  },
  buyer: 'TOOM Purchasing',
  paymentTerms: 'Net 30',
  fobPoint: 'Origin',
  shippingTerms: 'Prepaid',
  shipVia: 'TransLoop Logistics',
  dateScheduled: '07/31/2026',
  poNotes: 'Supplier must reference PO-11 on all shipping and invoice documentation.',
  items: [
    {
      itemType: 'Raw Material',
      number: '50012',
      description: 'Non-GMO Expeller Pressed Canola Oil',
      qtyOrdered: '42000',
      uom: 'Pound',
      unitCost: '0.95',
    },
  ],
  instructions:
    'Supplier should reference the PO number on invoices and delivery documentation. Contact TOOM before changing quantity, price, or delivery timing.',
}
